import { useTranslation } from '@gimlite/common';
import { useStoreUIV1, Wizard } from '@gimlite/ui-v1';
import { DefaultContractDetailsWizard } from '../../common/defaultValue/wizard';
import { CredentialType } from '../../common/mapper/credential.mapper';
import { MasterContractStep } from '../../common/steps/masterContract/masterContract.step';
import { SubContractStep } from '../../common/steps/subContract/subContract.step';
import {
  ContractBaseFormType,
  ContractDetailsFormType,
} from '../../form/contract-create/contract-create.form';
import { ConfirmationStep } from './steps/confirmation/confirmation.step';
import { ContractStep } from './steps/contract/contract.step';
import { ParkerStep } from './steps/parker/parker.step';
import { SummaryStep } from './steps/summary/summary.step';

import { observer } from 'mobx-react-lite';
import {
  AntiPassBackMode,
  ArticleCode,
  ContractEntity,
  PoolContractEntity,
} from '../../client/graphql';
import { portal } from '../../config';

export type AddOneMotoristToAnExistingPoolContractWizardContext = {
  parkingId: string | null;
  product: {
    id: string | null;
    credentials: CredentialType.GlobalName[];
    articleCodes: Array<ArticleCode>;
  };
  masterPoolContractId: string | null;
  subPoolContractId: string | null;
  parentContract: ContractBaseFormType.Data.ContractBase | null;
  contractBase: ContractBaseFormType.Data.ContractBase;
  credentials: ContractDetailsFormType.Data.Credentials;
  contractDetails: {
    create: ContractDetailsFormType.Data.ContractDetails;
    exist: ContractEntity | null;
  };
  addFakeQRCODE: boolean;
  selectExistContract: string | null;
  contractMode: 'create' | 'exist';
  contractExistedSave: ContractEntity[];
  subPoolExistedSave: PoolContractEntity[];
  masterPoolExistedSave: PoolContractEntity[];
  mode: typeof portal;
  skipSubContract: (skip: boolean) => void;
};

export const AddOneMotoristToAnExistingPoolContractWizard = observer(() => {
  const { t, lang } = useTranslation();
  const { WizardStore } = useStoreUIV1();
  const { parkingId, skipSubContract, mode } = WizardStore?.selected?.store;

  return (
    <Wizard<AddOneMotoristToAnExistingPoolContractWizardContext>
      config={{
        title: t(`addOneMotoristToAnExistingPoolContract`),
      }}
      data={{
        defaultContext: {
          parkingId,
          product: {
            id: null,
            credentials: [],
            articleCodes: [],
          },
          masterPoolContractId: null,
          subPoolContractId: null,
          parentContract: null,
          contractBase: {
            startDate: new Date().toISOString(),
            endDate: null,
            APBMode: AntiPassBackMode.Hard,
            APBNext: false,
            reference: null,
            description: null,
            state: true,
            articleCode: null,
          },
          addFakeQRCODE: true,
          credentials: {
            actived: [],
            desactived: [],
            context: 'wizardNewContract',
          },
          contractDetails: {
            create: DefaultContractDetailsWizard({ t, lang }),
            exist: null,
          },
          selectExistContract: null,
          contractMode: 'create',
          contractExistedSave: [],
          subPoolExistedSave: [],
          masterPoolExistedSave: [],
          mode,
          skipSubContract: (skip) => {
            WizardStore.updateStore({
              skipSubContract: skip,
            });
          },
        },
      }}
      handleEvent={{
        done: async (data) => {
          console.log('done', { data });
        },
        clear: () => {
          console.log('CLEAR');
        },
        close: () => {
          WizardStore.clear();
        },
      }}
    >
      <Wizard.Item
        config={{ title: t('masterContract'), component: MasterContractStep }}
      />
      {!skipSubContract && (
        <Wizard.Item
          config={{ title: t('subContract'), component: SubContractStep }}
        />
      )}
      <Wizard.Item config={{ title: t('contract'), component: ContractStep }} />
      <Wizard.Item config={{ title: t('parker'), component: ParkerStep }} />
      <Wizard.Item
        config={{ title: t('confirmation'), component: ConfirmationStep }}
      />
      <Wizard.Item config={{ title: t('summary'), component: SummaryStep }} />
    </Wizard>
  );
});
